import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import type { QuestionCategory, QuestionRate } from '@/pages/analytics/types';
import { formatPotentialPp } from '@/pages/analytics/utils/questionDeepDive';
import {
  buildQuestionCategoryTabs,
  formatQuestionCategoryLabel,
  normalizeQuestionCategory,
  type QuestionCategoryFilter,
} from '@/pages/analytics/utils/questionRateTabs';
import { buildAnalyticsQuestionDetailPath } from '@/pages/analytics/utils/resolveOrg';
import { cn } from '@/utils';

import { ChartCard } from './ChartCard';
import type { ChartDownloadFormat } from './ChartDownloadMenu';
import { ChartEmptyState } from './ChartStates';
import { InfoTooltip } from './InfoTooltip';

type QuestionView = 'passed' | 'missed';

interface QuestionPassRatesProps {
  data: QuestionRate[];
  loading?: boolean;
  onDownload?: (
    format: ChartDownloadFormat,
    exportElement?: HTMLElement | null,
  ) => void | Promise<void>;
  exportSectionId?: string;
  // When set, question rows link to the per-question deep-dive (AQ-608), carrying
  // the current filters. Omitted (e.g. in contexts with no org) → non-clickable.
  organizationId?: string | null;
  // Agent-detail context: the agent id lives in the route path, not searchParams,
  // so it must be threaded in explicitly to scope the deep-dive to this agent.
  // Omitted (org tab) → deep-dive stays org-wide.
  agentId?: string | null;
}

const CATEGORY_BADGE_CLASS: Record<QuestionCategory, string> = {
  compliance: 'bg-critical text-critical-foreground',
  communication: 'bg-positive text-positive-foreground',
  content: 'bg-attention text-attention-foreground',
};

// Backend types `category` as plain string (so future sections can be added
// without a schema bump). Map unknown categories to a neutral muted style.
const categoryBadgeClass = (category: string): string =>
  CATEGORY_BADGE_CLASS[normalizeQuestionCategory(category) as QuestionCategory] ??
  'bg-muted text-muted-foreground';

const formatPercent = (v: number) => `${Math.round(v * 100)}%`;
const getMissedCount = (row: QuestionRate) => row.total_count - row.passed_count;
const getMissedRate = (row: QuestionRate) => 1 - row.pass_rate;
const getPotential = (row: QuestionRate) => row.potential_pp ?? 0;
const isOverallRow = (row: QuestionRate) => row.group === 'overall';

// Sortable columns in the table. Default is Improvement Potential desc — the
// biggest coaching lift first, matching the question deep-dive's agent table.
type SortField = 'rate' | 'potential';
interface SortState {
  field: SortField;
  direction: 'asc' | 'desc';
}
const DEFAULT_SORT: SortState = { field: 'potential', direction: 'desc' };
const getRateTextClass = (view: QuestionView) =>
  view === 'passed' ? 'text-success' : 'text-destructive';
const getViewBadgeClass = (view: QuestionView) =>
  view === 'passed'
    ? 'border-positive-border bg-positive text-positive-foreground'
    : 'border-critical-border bg-critical text-critical-foreground';
// Mirrors the prototype's auto-layout table: every column is proportional (fr)
// with a sensible minimum, so the whole table fills the card width at any size
// instead of leaving fixed numeric columns stranded at the far right. Ratios
// approximate the prototype (Question widest; the numeric columns right-aligned).
const QUESTION_RATE_GRID =
  'grid-cols-[minmax(240px,2.6fr)_minmax(110px,1fr)_minmax(72px,0.7fr)_minmax(140px,1.2fr)_minmax(150px,1.8fr)]';

// SortHeader renders a clickable column header. Following the repo's GenericTable
// convention: the label keeps the same muted header style whether or not the
// column is the active sort — only the trailing indicator changes (ArrowUpDown
// when inactive, ArrowUp/ArrowDown for the active direction).
const SortHeader: React.FC<{
  label: string;
  active: boolean;
  direction: 'asc' | 'desc';
  onClick: () => void;
}> = ({ label, active, direction, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    className="flex cursor-pointer select-none items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground"
  >
    {label}
    {active ? (
      direction === 'asc' ? (
        <ArrowUp className="h-3 w-3 text-foreground" />
      ) : (
        <ArrowDown className="h-3 w-3 text-foreground" />
      )
    ) : (
      <ArrowUpDown className="h-3 w-3" />
    )}
  </button>
);

const QuestionRowsSkeleton: React.FC = () => (
  <div
    className="flex flex-col gap-2 py-0.5"
    role="status"
    aria-busy="true"
    aria-label="Loading question pass and miss rates"
  >
    {Array.from({ length: 6 }).map((_, index) => (
      <div key={index} className={cn('grid min-w-[760px] items-center gap-3', QUESTION_RATE_GRID)}>
        <Skeleton className="h-3.5 w-56 max-w-[48vw]" />
        <Skeleton className="h-5 w-24 rounded-md" />
        <Skeleton className="h-4 w-12 justify-self-end" />
        <Skeleton className="h-4 w-28 justify-self-end" />
        <Skeleton className="h-4 w-16 justify-self-end" />
      </div>
    ))}
  </div>
);

export const QuestionPassRates: React.FC<QuestionPassRatesProps> = ({
  data,
  loading,
  onDownload,
  exportSectionId,
  organizationId,
  agentId,
}) => {
  const [view, setView] = useState<QuestionView>('missed');
  const [category, setCategory] = useState<QuestionCategoryFilter>('all');
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [searchParams] = useSearchParams();

  // Clicking a sortable header toggles direction if already active, else selects
  // it descending (the useful default for both rate and potential).
  const toggleSort = (field: SortField) =>
    setSort(prev =>
      prev.field === field
        ? { field, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
        : { field, direction: 'desc' },
    );

  const buildQuestionHref = (metricKey: string): string => {
    if (!organizationId) return '';
    const params = new URLSearchParams(searchParams);
    // On agent detail the agent id is a route path param, absent from searchParams;
    // set it so the deep-dive (list + agent breakdown) scopes to this agent.
    if (agentId) params.set('agent_ids', agentId);
    const query = params.toString();
    const path = buildAnalyticsQuestionDetailPath(organizationId, metricKey);
    return query ? `${path}?${query}` : path;
  };

  const labeledRows = useMemo(
    () => data.filter(r => isOverallRow(r) && r.metric_label && r.metric_label.trim() !== ''),
    [data],
  );

  const categoryTabs = useMemo(() => buildQuestionCategoryTabs(labeledRows), [labeledRows]);

  const filtered = useMemo(() => {
    // Hide rows with no metric_label — the backend ships placeholder rows
    // for unmapped scorecard questions which we don't want to surface.
    return category === 'all'
      ? labeledRows
      : labeledRows.filter(r => normalizeQuestionCategory(r.category) === category);
  }, [category, labeledRows]);

  const visibleRows = useMemo(() => {
    // The "missed" view only lists questions that were actually missed.
    const rows =
      view === 'passed' ? [...filtered] : filtered.filter(row => getMissedCount(row) > 0);

    // Primary key is the displayed rate (pass rate in the passed view, miss rate
    // in the missed view) or the improvement potential, per the active sort.
    // Rate ties break on the matching count; both fall back to label for a
    // stable order. dir flips the primary comparison; ties stay deterministic.
    const dir = sort.direction === 'asc' ? -1 : 1;
    const primary = (row: QuestionRate): number =>
      sort.field === 'potential'
        ? getPotential(row)
        : view === 'passed'
          ? row.pass_rate
          : getMissedRate(row);

    return rows.sort((a, b) => {
      const primaryDelta = (primary(b) - primary(a)) * dir;
      if (primaryDelta !== 0) return primaryDelta;

      const countDelta =
        view === 'passed' ? b.passed_count - a.passed_count : getMissedCount(b) - getMissedCount(a);
      if (countDelta !== 0) return countDelta;

      return a.metric_label.localeCompare(b.metric_label);
    });
  }, [filtered, view, sort]);

  return (
    <ChartCard
      title="Question Pass & Miss Rates"
      info="Per-question performance across scorecard sections. Toggle between pass rate (how often the question was met) and miss rate (how often it was missed). 'All' shows the top questions; section tabs show every question in that section."
      titleAccessory={
        <Badge
          variant="outline"
          className={cn('h-5 px-2 text-[10px] font-semibold leading-4', getViewBadgeClass(view))}
        >
          {view === 'passed' ? 'Passed' : 'Missed'}
        </Badge>
      }
      rightSlot={
        <div className="inline-flex items-center gap-1 rounded-lg bg-muted/60 p-0.5">
          {(['passed', 'missed'] as const).map(v => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={cn(
                'rounded-md px-2.5 py-1 text-xs font-medium capitalize transition-colors',
                view === v
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-background/70 hover:text-foreground',
              )}
            >
              {v}
            </button>
          ))}
        </div>
      }
      onDownload={onDownload}
      exportSectionId={exportSectionId}
      className="h-full"
      bodyClassName="pt-0 pb-4 px-4 space-y-3"
    >
      <div
        className="inline-flex flex-wrap items-center gap-1 rounded-lg bg-muted/60 p-0.5 w-fit"
        data-export-hidden
      >
        {categoryTabs.map(tab => {
          const active = tab.value === category;
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => setCategory(tab.value)}
              className={cn(
                'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                active
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-background/70 hover:text-foreground',
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <QuestionRowsSkeleton />
      ) : visibleRows.length === 0 ? (
        <ChartEmptyState height={180} message="No metrics in this category." />
      ) : (
        // Fixed-height scroll region so the card doesn't stretch with the
        // full question list (this list can run 20–30+ rows). Category tabs
        // above remain anchored while the list scrolls.
        <div className="max-h-[360px] overflow-auto pr-1" data-export-scroll-region>
          <div className="min-w-[760px]">
            <div className={cn('grid items-center gap-3 border-b pb-1.5', QUESTION_RATE_GRID)}>
              <span className="px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Question
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Sections
              </span>
              <div className="flex justify-end">
                <SortHeader
                  label="Rate"
                  active={sort.field === 'rate'}
                  direction={sort.direction}
                  onClick={() => toggleSort('rate')}
                />
              </div>
              <span className="text-right text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Count
              </span>
              <div className="flex items-center justify-end gap-1 pr-3">
                <SortHeader
                  label="Improvement Potential"
                  active={sort.field === 'potential'}
                  direction={sort.direction}
                  onClick={() => toggleSort('potential')}
                />
                <InfoTooltip content="Estimated gain to the overall average score for the selected period if a question stops being missed. For example, if a question shows 2.5 pp, fixing it could lift the average score for the selected period from, say, 73.5 to 76." />
              </div>
            </div>
            <ul className="divide-y">
              {visibleRows.map(row => {
                const displayRate = view === 'passed' ? row.pass_rate : getMissedRate(row);
                const displayCount = view === 'passed' ? row.passed_count : getMissedCount(row);
                const countSuffix = view === 'passed' ? 'passed' : 'missed';
                const displayPercent = formatPercent(displayRate);
                const rowKey = [
                  row.group,
                  row.group_name,
                  row.metric_key,
                  row.total_count,
                  row.passed_count,
                ].join(':');

                const rowGridClass = cn('grid items-center gap-3 py-2', QUESTION_RATE_GRID);
                const rowContent = (
                  <>
                    <span
                      className="truncate px-1 text-[13px] font-medium leading-5 text-foreground"
                      title={row.metric_label}
                    >
                      {row.metric_label}
                    </span>
                    <Badge
                      variant="outline"
                      className={cn(
                        'h-5 w-fit border-transparent px-1.5 text-[10px] leading-4',
                        categoryBadgeClass(row.category),
                      )}
                    >
                      {formatQuestionCategoryLabel(row.category)}
                    </Badge>
                    <span
                      className={cn(
                        'text-right text-[13px] font-semibold leading-5 tabular-nums',
                        getRateTextClass(view),
                      )}
                    >
                      {displayPercent}
                    </span>
                    <span className="whitespace-nowrap text-right text-[13px] leading-5 text-muted-foreground tabular-nums">
                      {displayCount} / {row.total_count} {countSuffix}
                    </span>
                    <span className="whitespace-nowrap pr-3 text-right text-[13px] font-medium leading-5 text-success tabular-nums">
                      {formatPotentialPp(getPotential(row))}
                    </span>
                  </>
                );

                const href = row.metric_key ? buildQuestionHref(row.metric_key) : '';

                return (
                  <li key={rowKey} data-export-pdf-block>
                    {href ? (
                      <Link
                        to={href}
                        className={cn(
                          rowGridClass,
                          'rounded transition-colors hover:bg-muted/50 focus-visible:bg-muted/50',
                        )}
                        data-analytics-id="analytics:question:open"
                      >
                        {rowContent}
                      </Link>
                    ) : (
                      <div className={rowGridClass}>{rowContent}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </ChartCard>
  );
};
